/**
 * App_Floodgate.ts
 *
 * The entry point for floodgate.
 */

import { DynamicUxClientState, IFloodgateSettingIdMap, ISurvey, ISurveyForm, RoamingSettingIdMap } from "@ms-ofb/floodgate-shared-typings";
import { TelemetryLogger } from "@ms-ofb/officefloodgatecore";
import { setDynamicUxSchemaVariables } from "@ms-ofb/officefloodgatecore/dist/src/Campaign/CampaignDefinitionProvider";
import { TelemetryEvent } from "@ms-ofb/officefloodgatecore/dist/src/Constants";
import { renderCVSurvey } from "gatecrash";
import * as CommonApp from "./CommonApp";
import * as CommonFloodgate from "./CommonFloodgate";
import * as CommonUI from "./CommonUI";
import * as Configuration from "./Configuration/Configuration";
import { customerVoiceLauncherType } from "./Constants";
import FloodgateTelemetryLogger from "./FloodgateCore/FloodgateTelemetryLogger";
import ICustomSurvey from "./FloodgateCore/ICustomSurvey";
import CustomUISurvey from "./FloodgateCore/UISurvey/CustomUISurvey";
import DummyUISurvey from "./FloodgateCore/UISurvey/DummyUISurvey";
import IUISurvey from "./FloodgateCore/UISurvey/IUISurvey";
import UISurvey from "./FloodgateCore/UISurvey/UISurvey";
import { IHandleSurveyEvent } from "./IHandleSurveyEvent";
import { IOverrideRenderCallback } from "./IOverrideRenderCallback";
import { IContextParams, IRenderSurvey, ISurveyOptionalConfigParams, ISurveyRenderContext, IURLParams } from "./IRenderSurvey";
import * as Logging from "./Logging/Logging";
import * as DynamicUxClient from "./UI/DynamicUx/DynamicUxClient";
import { handleSurveyEvent, updateSurveyStyling } from "./UI/Toast/CVToast";
import * as UI from "./UI/UI";
import * as Window from "./Window/Window";

export { getEngineStartPromise, getStorageProvider, resetEngine, setEngineStartPromise, start, startInternal, stop } from "./CommonFloodgate";

/**
 * Has the control been opened. Prevents more than one dialog being generated.
 */
let opened = false;

/**
 * Initialize. Must be called before any other call to floodgate.
 * @returns A promise which will be rejected if the call fails.
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		const rejectAndLogError = (errorMessage: string) => {
			const telemetryLogger = TelemetryLogger.getTelemetryLogger();
			if (telemetryLogger) {
				telemetryLogger.log_Event(TelemetryEvent.Floodgate.Initialize.Failed, { ErrorMessage: errorMessage });
			}

			reject(errorMessage);
		};

		if (!CommonFloodgate.getInitialized()) {
			try {
				CommonApp.initialize();
			} catch (err) {
				reject("CommonApp initialization failed: {" + err + "}");
			}

			TelemetryLogger.setTelemetryLogger(new FloodgateTelemetryLogger());

			CommonUI.initialize()
				.then(
					function onInitializeFulfilled() {
						try {
							CommonFloodgate.initialize();
							initializeDynamicUx();
						} catch (err) {
							rejectAndLogError("CommonFloodgate initialization failed: {" + err + "}");
						}

						resolve(null);
					}
				)
				.catch(
					function onInitializeRejected(err) {
						rejectAndLogError("Initialization failed: {" + err + "}");
					}
				);
		} else {
			resolve(null);
		}
	});
}

function initializeDynamicUx() {
	try {
		// Setting the DynamicUxSchemaVariables regardless of the DynamicUx enabled, because the CampaignDefinitions will be 
		// deserialized in any cases, and we don't want to get deserialization failure logging.
		// Deserialization failure should be only be logged if CampaignDefinition is actually wrong.
		setDynamicUxSchemaVariables(Configuration.get().getDynamicUxConfig().dynamicVariables);
		if (Configuration.get().getDynamicUxConfig().enabled && Configuration.get().checkIfSurveysEnabled()) {
			DynamicUxClient.setup(Configuration.get().getDynamicUxConfig());
			DynamicUxClient.get().delayLoadCentroUx();
		}
	} catch (err) {
		Logging.getLogger().logEvent(Logging.EventIds.DYNAMICUX_INITIALIZATION_FAILURE,
			Logging.LogLevel.Error,
			{ ErrorMessage: "DynamicUx intialization failed: {" + err + "}" }
		);
		Configuration.get().resetDynamicUxConfig();
	}
}

/**
 * Display the given Survey with it's prompt
 * @param survey the survey to show
 * @param overrideUXRenderCallbackAsync a callback to override rendering
 * (currently only supports CV surveys)
 * @returns A promise which will be rejected if the call fails.
 */
export function showSurvey(
	survey: IUISurvey,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback
): Promise<void> {
	if (!Configuration.get().checkIfSurveysEnabled()) {
		return Promise.reject("Survey is disabled.");
	}

	if (survey.getUxSchema()) {
		if (Configuration.get().getDynamicUxConfig().enabled && Configuration.get().checkIfSurveysEnabled()) {
			const dynamicUxClient = DynamicUxClient.get();
			const status = dynamicUxClient.getStatus();

			// acting on DynamicUx status
			if (status === DynamicUxClientState.Success || status === DynamicUxClientState.Failure) {
				return showSurveyPromise(survey, overrideUXRenderCallbackAsync, status);
			}
			else if (status === DynamicUxClientState.Starting) {
				dynamicUxClient.setOnSurveyActivatedCallback((status: DynamicUxClientState) => {
					// Success or legacy content is available for survey trigger
					showSurveyPromise(survey, overrideUXRenderCallbackAsync, status).catch((error) => {
						Logging.getLogger().logEvent(Logging.EventIds.SURVEY_TRIGGER_DYNAMICUX_FAILURE,
							Logging.LogLevel.Critical,
							{
								CampaignId: survey.getCampaignId(),
								SurveyId: survey.getId(),
								SurveyType: survey.getSurveyType(),
								Message: JSON.stringify({
									DynamicUxClientState: status
								}),
								ErrorMessage: error && error.toString()
							}
						);
					});
				}, survey)
				return Promise.resolve();
			}
			else {
				// Shouldn't get here
				return Promise.reject("DynamicUx is not started");
			}
		}
		else if (!survey.isContentValid()) {
			return Promise.reject("No content available with DynamicUx is disabled");
		}
	}

	return showSurveyPromise(survey, overrideUXRenderCallbackAsync);
}

function showSurveyPromise(
	survey: IUISurvey,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback,
	dynamicUxStatus = DynamicUxClientState.Success
): Promise<void> {
	if (dynamicUxStatus === DynamicUxClientState.Success || (dynamicUxStatus === DynamicUxClientState.Failure && survey.isContentValid())) {
		return new Promise<void>((resolve, reject) => {
			if (opened) {
				reject("Control already open");
				return;
			}

			if (!Configuration.get().isFeedbackSurveyEnabledByAADC()) {
				Logging.getLogger().logEvent(Logging.EventIds.SURVEY_UI_DISABLED_AADC,
					Logging.LogLevel.Error,
					{
						CampaignId: survey.getCampaignId(),
						Data: JSON.stringify({
							ageGroup: Configuration.get().getCommonInitOptions().ageGroup,
							authenticationType: Configuration.get().getCommonInitOptions().authenticationType,
						}),
					});
				reject("Prompt is prohibited by AADC");
				return;
			}

			Configuration.get().setFloodgateSurvey(survey ? survey : new DummyUISurvey());
			const isCVSurveyEnabled = Configuration.get().isCVSurveyEnabled();
			/**
			 * Branching logic to determine if Customer Voice survey is shown to user.
			 */
			if (isCVSurveyEnabled && survey.getLauncherType() === customerVoiceLauncherType) {
				const contextParams: IContextParams = getContextParams(survey);
				const optionalConfigParams: ISurveyOptionalConfigParams = getOptionalConfigParams();

				const renderContext: ISurveyRenderContext = UI.createCVSurvey(
					contextParams,
					function () { opened = false; },
					optionalConfigParams,
					overrideUXRenderCallbackAsync
				);
				Configuration.get().setCVRenderContext(renderContext);
				opened = true;
				resolve();
				return;
			}
			UI.createSurvey(function () { opened = false; });
			opened = true;
			resolve();
		});
	}
	else {
		return Promise.reject("Survey Content is not valid or DynamicUx failed to load")
	}
}

/**
 * Method to create the context params object
 * @param survey the survey being shown
 */
function getContextParams(survey: IUISurvey): IContextParams {
	const urlParams: IURLParams = {};
	if (Configuration.get().getCommonInitOptions().primaryColour) {
		urlParams.appPrimaryColor = Configuration.get().getCommonInitOptions().primaryColour.replace("#", "");
	}
	if (Configuration.get().getCommonInitOptions().secondaryColour) {
		urlParams.appSecondaryColor = Configuration.get().getCommonInitOptions().secondaryColour.replace("#", "");
	}

	return {
		customerVoiceFormId: survey.getCVSurveyFormId(),
		flights: Configuration.get().getCVSurveyFlights(),
		isCommercialHost: Configuration.get().getCommonInitOptions().isCommercialHost,
		locale: Configuration.get().getCommonInitOptions().locale,
		platform: Configuration.get().getCommonInitOptions().telemetryGroup.platform,
		showUserEmailAddress: Configuration.get().getFloodgateInitOptions().showEmailAddress,
		productName: Configuration.get().getProductName(),
		uiHost: Configuration.get().getUIHost(),
		uiHostCVSurveyDispatcher: Configuration.get().getUIHostCVSurveyDispatcher(),
		stylesUrl: Configuration.get().getCommonInitOptions().stylesUrl,
		urlParams,
		isCopilotLicenseEnabled: Configuration.get().isCopilotLicenseEnabled(),

	};
}

/**
 * Method to create the optional config params
 * @param survey the survey being shown
 */
function getOptionalConfigParams(): ISurveyOptionalConfigParams {
	const optionalConfigParams: ISurveyOptionalConfigParams = {
		closeButtonEnabled: true,
		cancelButtonEnabled: true,
		timeOutEnabled: true,
	};
	if (Configuration.get().getCSSPosition()) {
		optionalConfigParams.cssPosition = Configuration.get().getCSSPosition();
	}
	if (Configuration.get().getCSSZIndex()) {
		optionalConfigParams.cssZIndex = Configuration.get().getCSSZIndex();
	}
	if (Configuration.get().getCSSBottom()) {
		optionalConfigParams.cssBottom = Configuration.get().getCSSBottom();
	}
	if (Configuration.get().getCSSRight()) {
		optionalConfigParams.cssRight = Configuration.get().getCSSRight();
	}
	if (Configuration.get().getRuntimeCssVars()) {
		optionalConfigParams.runtimeCssVars = Configuration.get().getRuntimeCssVars();
	}
	if (Configuration.get().getRuntimeHtmlDataAttrs()) {
		optionalConfigParams.runtimeHtmlDataAttrs = Configuration.get().getRuntimeHtmlDataAttrs();
	}
	return optionalConfigParams;
}

/**
 * Method to allow users to launch a survey from an ISurveyForm
 * @param survey the survey to show
 * @param overrideUXRenderCallbackAsync a callback to override rendering
 * (currently only supports CV surveys)
 */
export function showFormSurvey(
	survey: ISurveyForm,
	overrideUXRenderCallbackAsync?: IOverrideRenderCallback
): Promise<any> {
	const genericSurvey: ISurvey = survey as ISurvey;

	const formattedSurvey: IUISurvey = new UISurvey(genericSurvey);
	return showSurvey(formattedSurvey, overrideUXRenderCallbackAsync);
}

/**
 * Method to allow users to launch a custom survey directly
 * @param survey the survey to show
 * @returns A promise which will be rejected if the call fails.
 */
export function showCustomSurvey(survey: ICustomSurvey): Promise<any> {

	if (!CommonFloodgate.getInitialized()) {
		return new Promise((resolve, reject) => {

			Logging.getLogger().logEvent(Logging.EventIds.SURVEY_FLOODGATE_SHOWCUSTOMSURVEY_FAILED,
				Logging.LogLevel.Error,
				{
					ErrorMessage: "App_Floodgate.showCustomSurvey called when CommonFloodgate has not initialized",
					CampaignId: survey?.campaignId,
				},
				0.01 /* 1% internalSampleRate */
			);

			reject("Floodgate has not been initialized.");
		});
	}

	return showSurvey(new CustomUISurvey(survey), null);
}

/**
 * Method to allow users to call survey rendering independent from showSurvey().
 * @param parentElement the container in which the survey will be rendered
 * @param onSurveyEvent a handler for all events emitted by survey tools
 * @param contextParams information about the context in which the survey is shown
 * @param surveyOptionalConfigParams additional survey configuration parameters
 */
export const renderSurvey: IRenderSurvey = (
	parentElement: string | HTMLElement,
	onSurveyEvent: IHandleSurveyEvent,
	contextParams: IContextParams,
	surveyOptionalConfigParams?: ISurveyOptionalConfigParams
): void => {
	const isMainContext = Configuration.get().getFloodgateSurvey() ? true : false;

	// If a CV form ID is provided, we classify this request as a Customer Voice survey
	const formIdBool = !!contextParams.customerVoiceFormId;
	if (formIdBool) {
		renderCVSurvey(
			parentElement,
			onSurveyEvent,
			contextParams,
			surveyOptionalConfigParams,
			isMainContext
		);
	} else {
		const telemetryLogger = TelemetryLogger.getTelemetryLogger();
		if (telemetryLogger) {
			telemetryLogger.log_Event(
				TelemetryEvent.Floodgate.RenderSurvey.Unsupported,
				{ ErrorMessage: `WARN: renderSurvey currently only supports Customer Voice surveys.` }
			);
		}
	}
};

/**
 * Reset the floodgate module. Used in unit tests.
 */
export function reset() {
	CommonApp.reset();
	CommonUI.reset();
	CommonFloodgate.reset();
	opened = false;
}

/**
 * Get a list of setting names and ids
 * @returns the setting names and ids list
 */
export function getSettingIdMap(): IFloodgateSettingIdMap {
	return RoamingSettingIdMap;
}

Window.setFloodgateCustomUXHandler({ renderSurvey, handleSurveyEvent, updateSurveyStyling });
Window.setFloodgateShowSurvey(showSurvey);
Window.setFloodgateShowFormSurvey(showFormSurvey);
Window.setFloodgateShowCustomSurvey(showCustomSurvey);
Window.setFloodgateInitialize(initialize);
Window.getSettingIdMap(getSettingIdMap);
